import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { ROLE_LABELS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BellRing, Loader2, Search } from "lucide-react";

const RecipientList = ({ users, sel, toggle, pid }) => (
  <div className="max-h-56 overflow-y-auto border border-border" data-testid="qr-recipient-list">
    {users.length === 0 && <div className="p-3 text-xs text-muted-foreground">No users found.</div>}
    {users.map((u) => (
      <label key={u.id} className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-1.5 text-sm last:border-0 hover:bg-secondary/60" data-testid={`qr-recipient-${u.id}`}>
        <Checkbox checked={sel.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
        <span className="font-medium">{u.name}</span>
        <span className="text-xs text-muted-foreground">{ROLE_LABELS[u.role] || u.role}{pid && u.property_ids.includes(pid) ? " · this property" : ""}</span>
        <span className="ml-auto truncate text-xs text-muted-foreground">{u.email}</span>
      </label>
    ))}
  </div>
);

export const QuickReminder = () => {
  const { activePropertyId: pid } = useActiveProperty();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/quick-reminders/recipients").then((r) => {
      setUsers(r.data);
      setSel(pid ? r.data.filter((u) => ["gm", "regional"].includes(u.role) && u.property_ids.includes(pid)).map((u) => u.id) : []);
    }).catch(() => setUsers([]));
  }, [open, pid]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t ? users.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(t)) : users;
    return [...list].sort((a, b) => (sel.includes(b.id) - sel.includes(a.id)) || ((pid && b.property_ids.includes(pid)) - (pid && a.property_ids.includes(pid))));
  }, [users, q, sel, pid]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/quick-reminders/send", { user_ids: sel, subject, message, property_id: pid || null });
      toast.success(`Reminder sent to ${r.data.sent_to.length} recipient(s)`);
      setOpen(false); setSubject(""); setMessage(""); setQ("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="quick-reminder-btn">
        <BellRing className="mr-1.5 h-4 w-4" /> Send Reminder
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="quick-reminder-dialog">
          <DialogHeader><DialogTitle>Send Reminder Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>To ({sel.length} selected){pid ? " — GM & Regional of this property pre-selected" : ""}</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users" className="pl-8" data-testid="qr-search" />
              </div>
              <RecipientList users={shown} sel={sel} toggle={toggle} pid={pid} />
            </div>
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="qr-subject" />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="qr-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={busy || !sel.length || !subject.trim() || !message.trim()} data-testid="qr-send">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
